import React, { useState, useEffect, useRef, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FlagTwoToneIcon from "@mui/icons-material/FlagTwoTone";
import StyleTwoToneIcon from "@mui/icons-material/StyleTwoTone";
import ExtensionTwoToneIcon from "@mui/icons-material/ExtensionTwoTone";
import AccessAlarmTwoToneIcon from "@mui/icons-material/AccessAlarmTwoTone";
import SettingsTwoToneIcon from "@mui/icons-material/SettingsTwoTone";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import type { PaletteColor } from "../types/PaletteColor.ts";
import { PaletteColorHex } from "../types/PaletteColor.ts";
import { availableEffects, EffectName } from "./effects.ts";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import MotionPhotosAutoSharpIcon from "@mui/icons-material/MotionPhotosAutoSharp";
import type { Tag, CustomColumn, AutoReminderRule } from "../types/types.ts";
import { AUTH_KEY_PREFIX } from "../utils/authStorage.ts";
import { buildGotoUrl } from "../utils/chatisUrls.ts";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useTestMode, setTestModeEnabled } from "../hooks/useTestMode.ts";

interface RoomStatus {
  roomStatusId: number;
  statusName: string;
  color: PaletteColor;
  isDeletable: boolean;
  action: EffectName;
}

interface CustomColumnForm {
  columnName: string;
  columnType: "text" | "select";
  options: string[];
}

const emptyColumnForm = (): CustomColumnForm => ({
  columnName: "",
  columnType: "text",
  options: [""],
});

interface Props {
  roomId: number;
  onClose: (settingsChanged: boolean) => void;
  forceReload: () => void;
  tags: Tag[];
  onAddTag: (name: string, color: string) => Promise<void>;
  onDeleteTag: (tagId: number) => Promise<void>;
  onUpdateTag: (tagId: number, name: string, color: string) => Promise<void>;
  honbuId: number;
  activeListId?: number | null;
  todoLists?: { id: string; name: string }[];
  color?: PaletteColor;
}

/** 現在の表示モード。/goto へ引き継いで遷移先の見た目を揃える */
const isDarkModeFromUrl = (): boolean =>
  new URLSearchParams(window.location.search).get("mode") === "dark";

/**
 * ショートカット起動中かどうか。
 * Chatis の iframe 内で shortcut=1 を付けるとショートカットヘッダーが出てしまうため、
 * 現在の URL に付いていた場合のみ引き継ぐ。
 */
const isShortcutLaunch = (): boolean =>
  new URLSearchParams(window.location.search).get("shortcut") === "1";

const PALETTE_COLORS = Object.values(PaletteColorHex);

type TabKey = "status" | "other" | "tags" | "custom" | "autoReminder";

export function GeneralSettingsDialog({
  roomId,
  onClose,
  forceReload,
  tags,
  onAddTag,
  onDeleteTag,
  onUpdateTag,
  honbuId,
  activeListId,
  todoLists,
  color = "Blue",
}: Props) {
  const accent = PaletteColorHex[color];
  const [activeTab, setActiveTab] = useState<TabKey>("status");
  const [mobileOpenSection, setMobileOpenSection] = useState<TabKey | null>(null);

  const toggleMobileSection = (section: TabKey) => {
    const next = mobileOpenSection === section ? null : section;
    setMobileOpenSection(next);
    if (next) setActiveTab(next);
  };

  // Status state
  const [statuses, setStatuses] = useState<RoomStatus[]>([]);
  const [newStatusName, setNewStatusName] = useState("");
  const [newStatusColor, setNewStatusColor] = useState<PaletteColor>("Gray");
  const [openColorPickerId, setOpenColorPickerId] = useState<number | null>(
    null,
  );
  const [openEffectPickerId, setOpenEffectPickerId] = useState<number | null>(
    null,
  );
  const [isNewColorPickerOpen, setIsNewColorPickerOpen] = useState(false);

  // Tag state
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(PALETTE_COLORS[0]);
  const [editingTagId, setEditingTagId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");

  // Custom column state
  const [columnsByListId, setColumnsByListId] = useState<Record<number, CustomColumn[]>>({});
  const [addingToListId, setAddingToListId] = useState<number | null>(null);
  const [columnForm, setColumnForm] = useState<CustomColumnForm>(emptyColumnForm());
  const [editingColumnId, setEditingColumnId] = useState<number | null>(null);
  const [editColumnForm, setEditColumnForm] = useState<CustomColumnForm>(emptyColumnForm());
  const [savingColumn, setSavingColumn] = useState(false);
  const [columnError, setColumnError] = useState<string | null>(null);
  // 選択肢の一括作成（改行区切りテキストから選択肢を生成）
  const [bulkOptionTarget, setBulkOptionTarget] = useState<"add" | "edit" | null>(null);
  const [bulkOptionText, setBulkOptionText] = useState("");

  const closeBulkOptionInput = () => {
    setBulkOptionTarget(null);
    setBulkOptionText("");
  };

  const applyBulkOptions = () => {
    const lines = bulkOptionText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l !== "");
    if (lines.length > 0) {
      const setForm = bulkOptionTarget === "edit" ? setEditColumnForm : setColumnForm;
      setForm((f) => ({
        ...f,
        options: [...f.options.filter((o) => o.trim() !== ""), ...lines],
      }));
    }
    closeBulkOptionInput();
  };

  // Auto reminder state — プロジェクトごとに複数ルールを持つ
  interface AutoReminderRuleForm {
    autoReminderRuleId: number; // <= 0 は未保存の新規ルール（クライアント一時ID）
    ruleName: string;
    isEnabled: boolean;
    daysBeforeDeadline: number;
    reminderHour: number;
    enabledPriorities: string[];
    enabledStatuses: string[];
    enabledTags: number[];
    applyToExisting: boolean;
  }
  const tempRuleIdRef = useRef(-1);
  const newAutoReminderRuleForm = (): AutoReminderRuleForm => ({
    autoReminderRuleId: tempRuleIdRef.current--,
    ruleName: "",
    isEnabled: true,
    daysBeforeDeadline: 1,
    reminderHour: 9,
    enabledPriorities: ["高", "中", "低", "なし"],
    enabledStatuses: ["未対応", "対応中", "待機"],
    enabledTags: [],
    applyToExisting: true,
  });
  const [autoReminderRules, setAutoReminderRules] = useState<Record<number, AutoReminderRuleForm[]>>({});
  const [autoReminderLoaded, setAutoReminderLoaded] = useState<Record<number, boolean>>({});
  const [loadingAutoReminder, setLoadingAutoReminder] = useState<Record<number, boolean>>({});
  const [savingAutoRuleId, setSavingAutoRuleId] = useState<number | null>(null);
  const [autoRuleSaveResult, setAutoRuleSaveResult] = useState<Record<number, "ok" | "error">>({});
  // 「＋ タグを追加」ピッカーを開いているルールID（ルールIDは全リストを通して一意）
  const [tagPickerRuleId, setTagPickerRuleId] = useState<number | null>(null);

  const fetchAutoReminderRules = async (listId: number) => {
    setLoadingAutoReminder((prev) => ({ ...prev, [listId]: true }));
    try {
      const res = await fetch(`/api/todolists/${listId}/auto-reminder-rules`);
      if (res.ok) {
        const rules: AutoReminderRule[] = await res.json();
        setAutoReminderRules((prev) => ({
          ...prev,
          [listId]: rules.map((r) => ({
            autoReminderRuleId: r.autoReminderRuleId,
            ruleName: r.ruleName ?? "",
            isEnabled: r.isEnabled,
            daysBeforeDeadline: r.daysBeforeDeadline,
            reminderHour: r.reminderHour,
            enabledPriorities: r.enabledPriorities,
            enabledStatuses: r.enabledStatuses,
            enabledTags: r.enabledTags ?? [],
            applyToExisting: false,
          })),
        }));
      }
      setAutoReminderLoaded((prev) => ({ ...prev, [listId]: true }));
    } catch {
      setAutoReminderLoaded((prev) => ({ ...prev, [listId]: true }));
    } finally {
      setLoadingAutoReminder((prev) => ({ ...prev, [listId]: false }));
    }
  };

  const addAutoReminderRule = (listId: number) => {
    setAutoReminderRules((prev) => ({
      ...prev,
      [listId]: [...(prev[listId] ?? []), newAutoReminderRuleForm()],
    }));
  };

  const updateAutoReminderRuleForm = (
    listId: number,
    ruleId: number,
    updates: Partial<AutoReminderRuleForm>,
  ) => {
    setAutoReminderRules((prev) => ({
      ...prev,
      [listId]: (prev[listId] ?? []).map((r) =>
        r.autoReminderRuleId === ruleId ? { ...r, ...updates } : r,
      ),
    }));
  };

  const replaceRuleId = (listId: number, oldId: number, newRule: AutoReminderRule) => {
    setAutoReminderRules((prev) => ({
      ...prev,
      [listId]: (prev[listId] ?? []).map((r) =>
        r.autoReminderRuleId === oldId
          ? { ...r, autoReminderRuleId: newRule.autoReminderRuleId, applyToExisting: false }
          : r,
      ),
    }));
  };

  const flashSaveResult = (ruleId: number, result: "ok" | "error") => {
    setAutoRuleSaveResult((prev) => ({ ...prev, [ruleId]: result }));
    if (result === "ok") {
      setTimeout(
        () =>
          setAutoRuleSaveResult((prev) => {
            const n = { ...prev };
            delete n[ruleId];
            return n;
          }),
        2000,
      );
    }
  };

  const handleSaveAutoReminderRule = async (listId: number, rule: AutoReminderRuleForm) => {
    setSavingAutoRuleId(rule.autoReminderRuleId);
    const isNew = rule.autoReminderRuleId <= 0;
    const url = isNew
      ? `/api/todolists/${listId}/auto-reminder-rules`
      : `/api/auto-reminder-rules/${rule.autoReminderRuleId}`;
    try {
      const res = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ruleName: rule.ruleName.trim() || null,
          isEnabled: rule.isEnabled,
          daysBeforeDeadline: rule.daysBeforeDeadline,
          reminderHour: rule.reminderHour,
          enabledPriorities: rule.enabledPriorities,
          enabledStatuses: rule.enabledStatuses,
          enabledTags: rule.enabledTags,
          applyToExisting: rule.applyToExisting,
        }),
      });
      if (res.ok) {
        const saved: AutoReminderRule = await res.json();
        if (isNew) replaceRuleId(listId, rule.autoReminderRuleId, saved);
        else updateAutoReminderRuleForm(listId, rule.autoReminderRuleId, { applyToExisting: false });
        flashSaveResult(saved.autoReminderRuleId, "ok");
      } else {
        flashSaveResult(rule.autoReminderRuleId, "error");
      }
    } catch {
      flashSaveResult(rule.autoReminderRuleId, "error");
    } finally {
      setSavingAutoRuleId(null);
    }
  };

  const removeRuleFromState = (listId: number, ruleId: number) => {
    setAutoReminderRules((prev) => ({
      ...prev,
      [listId]: (prev[listId] ?? []).filter((r) => r.autoReminderRuleId !== ruleId),
    }));
  };

  const handleDeleteAutoReminderRule = (listId: number, rule: AutoReminderRuleForm) => {
    // 未保存の新規ルールはローカルから削除するだけ
    if (rule.autoReminderRuleId <= 0) {
      removeRuleFromState(listId, rule.autoReminderRuleId);
      return;
    }
    setConfirmDialog({
      isOpen: true,
      title: "自動リマインダールールの削除",
      message:
        "このルールを削除しますか？\nこのルールから自動作成されたリマインダーも削除されます。",
      type: "danger",
      confirmText: "削除",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        try {
          const res = await fetch(`/api/auto-reminder-rules/${rule.autoReminderRuleId}`, {
            method: "DELETE",
          });
          if (res.ok) removeRuleFromState(listId, rule.autoReminderRuleId);
        } catch {
          // silent
        }
      },
    });
  };

  const [settingsModified, setSettingsModified] = useState(false);
  const effectPickerRef = useRef<HTMLDivElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const newColorPickerRef = useRef<HTMLDivElement>(null);

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "normal" | "danger";
    confirmText: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "確認",
    message: "",
    type: "normal",
    confirmText: "OK",
    onConfirm: () => {},
  });

  const fetchColumnsForList = async (listId: number) => {
    try {
      const res = await fetch(`/api/todolists/${listId}/custom-columns?honbuId=${honbuId}`);
      const cols: CustomColumn[] = res.ok ? await res.json() : [];
      setColumnsByListId((prev) => ({ ...prev, [listId]: cols }));
    } catch {
      setColumnsByListId((prev) => ({ ...prev, [listId]: [] }));
    }
  };

  useEffect(() => {
    if (activeTab !== "custom") return;
    const lists =
      todoLists && todoLists.length > 0
        ? todoLists
        : activeListId
          ? [{ id: String(activeListId) }]
          : [];
    for (const list of lists) {
      const listId = parseInt(list.id, 10);
      if (listId > 0) fetchColumnsForList(listId);
    }
  }, [activeTab, todoLists, activeListId, honbuId]);

  useEffect(() => {
    if (activeTab !== "autoReminder") return;
    const lists =
      todoLists && todoLists.length > 0
        ? todoLists
        : activeListId
          ? [{ id: String(activeListId), name: "現在のプロジェクト" }]
          : [];
    for (const list of lists) {
      const listId = parseInt(list.id, 10);
      if (listId > 0 && !autoReminderLoaded[listId]) {
        fetchAutoReminderRules(listId);
      }
    }
  }, [activeTab, todoLists, activeListId]);

  const handleAddColumn = async (listId: number) => {
    if (!columnForm.columnName.trim()) { setColumnError("項目名を入力してください"); return; }
    if (columnForm.columnType === "select" && columnForm.options.filter(o => o.trim()).length === 0) {
      setColumnError("選択肢を1つ以上入力してください"); return;
    }
    setSavingColumn(true);
    setColumnError(null);
    try {
      const res = await fetch(`/api/todolists/${listId}/custom-columns?honbuId=${honbuId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          columnName: columnForm.columnName.trim(),
          columnType: columnForm.columnType,
          options: columnForm.columnType === "select"
            ? columnForm.options.filter(o => o.trim()).map(o => o.trim())
            : null,
        }),
      });
      if (!res.ok) throw new Error();
      const created: CustomColumn = await res.json();
      setColumnsByListId((prev) => ({ ...prev, [listId]: [...(prev[listId] || []), created] }));
      setSettingsModified(true);
      setColumnForm(emptyColumnForm());
      setAddingToListId(null);
    } catch {
      setColumnError("保存に失敗しました");
    } finally {
      setSavingColumn(false);
    }
  };

  const handleUpdateColumn = async (columnId: number) => {
    if (!editColumnForm.columnName.trim()) { setColumnError("項目名を入力してください"); return; }
    if (editColumnForm.columnType === "select" && editColumnForm.options.filter(o => o.trim()).length === 0) {
      setColumnError("選択肢を1つ以上入力してください"); return;
    }
    setSavingColumn(true);
    setColumnError(null);
    try {
      let ownerListId = 0;
      let colForOrder: CustomColumn | undefined;
      for (const [lid, cols] of Object.entries(columnsByListId)) {
        const found = cols.find((c) => c.customColumnId === columnId);
        if (found) { ownerListId = parseInt(lid); colForOrder = found; break; }
      }
      const res = await fetch(`/api/custom-columns/${columnId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          columnName: editColumnForm.columnName.trim(),
          columnType: editColumnForm.columnType,
          displayOrder: colForOrder?.displayOrder ?? 0,
          options: editColumnForm.columnType === "select"
            ? editColumnForm.options.filter(o => o.trim()).map(o => o.trim())
            : null,
        }),
      });
      if (!res.ok) throw new Error();
      const updated: CustomColumn = await res.json();
      if (ownerListId) {
        setColumnsByListId((prev) => ({
          ...prev,
          [ownerListId]: (prev[ownerListId] || []).map((c) =>
            c.customColumnId === columnId ? updated : c
          ),
        }));
      }
      setSettingsModified(true);
      setEditingColumnId(null);
    } catch {
      setColumnError("更新に失敗しました");
    } finally {
      setSavingColumn(false);
    }
  };

  const handleDeleteColumn = (columnId: number) => {
    setConfirmDialog({
      isOpen: true,
      title: "カスタム項目の削除",
      message: "このカスタム項目を削除しますか？\n設定済みの値もすべて削除されます。",
      type: "danger",
      confirmText: "削除",
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          const res = await fetch(`/api/custom-columns/${columnId}`, { method: "DELETE" });
          if (res.ok) {
            setColumnsByListId((prev) => {
              const next = { ...prev };
              for (const lid in next) {
                next[lid] = next[lid].filter((c) => c.customColumnId !== columnId);
              }
              return next;
            });
            setSettingsModified(true);
          }
        } catch {
          // silent
        }
      },
    });
  };

  const sortedListsForCustom = useMemo(() => {
    const lists =
      todoLists && todoLists.length > 0
        ? todoLists
        : activeListId
          ? [{ id: String(activeListId), name: "現在のプロジェクト" }]
          : [];
    const activeIdStr = String(activeListId);
    const active = lists.find((l) => l.id === activeIdStr);
    const others = lists.filter((l) => l.id !== activeIdStr);
    return active ? [active, ...others] : lists;
  }, [todoLists, activeListId]);

  const startEditColumn = (col: CustomColumn) => {
    setEditingColumnId(col.customColumnId);
    setEditColumnForm({
      columnName: col.columnName,
      columnType: col.columnType,
      options: col.options.length > 0 ? col.options.map(o => o.optionValue) : [""],
    });
    setColumnError(null);
    closeBulkOptionInput();
  };

  const fetchStatuses = async () => {
    try {
      const response = await fetch(
        `/api/rooms/${roomId}/statuses?honbuId=${honbuId}`,
      );
      if (response.ok) {
        const data = (await response.json()) as RoomStatus[];
        setStatuses(data);
      }
    } catch (error) {
      console.error("Failed to fetch statuses", error);
    }
  };

  useEffect(() => {
    if (activeTab === "status" || activeTab === "autoReminder") {
      fetchStatuses();
    }
  }, [roomId, activeTab, honbuId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target as Node)
      ) {
        setOpenColorPickerId(null);
      }
      if (
        effectPickerRef.current &&
        !effectPickerRef.current.contains(event.target as Node)
      ) {
        setOpenEffectPickerId(null);
      }
      if (
        newColorPickerRef.current &&
        !newColorPickerRef.current.contains(event.target as Node)
      ) {
        setIsNewColorPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleAddStatus = async () => {
    if (!newStatusName.trim()) return;
    const response = await fetch(`/api/rooms/${roomId}/statuses`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        statusName: newStatusName,
        color: newStatusColor,
        createdHonbuId: honbuId,
      }),
    });
    if (response.ok) {
      setSettingsModified(true);
      setNewStatusName("");
      setNewStatusColor("Gray");
      fetchStatuses();
    }
  };

  const handleUpdate = async (
    statusId: number,
    updateData: { color?: PaletteColor; action?: EffectName },
  ) => {
    const status = statuses.find((s) => s.roomStatusId === statusId);
    if (!status) return;

    const payload = {
      color: status.color,
      action: status.action,
      ...updateData,
    };

    const response = await fetch(`/api/statuses/${statusId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (response.ok) {
      setSettingsModified(true);
      fetchStatuses();
    }
  };

  const handleDeleteStatus = async (statusId: number) => {
    setConfirmDialog({
      isOpen: true,
      title: "ステータスの削除",
      message: "このステータスを削除しますか？",
      type: "danger",
      confirmText: "削除",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        const response = await fetch(`/api/statuses/${statusId}`, {
          method: "DELETE",
        });
        if (response.ok) {
          setSettingsModified(true);
          fetchStatuses();
        }
      },
    });
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source } = result;
    if (!destination) return;

    const newStatuses = Array.from(statuses);
    const [reorderedItem] = newStatuses.splice(source.index, 1);
    newStatuses.splice(destination.index, 0, reorderedItem);
    setStatuses(newStatuses);

    const orderedIds = newStatuses.map((s) => s.roomStatusId);
    const response = await fetch(
      `/api/rooms/${roomId}/statuses/order?honbuId=${honbuId}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderedStatusIds: orderedIds }),
      },
    );
    if (response.ok) {
      setSettingsModified(true);
    }
  };

  const handleCacheClear = async () => {
    setConfirmDialog({
      isOpen: true,
      title: "キャッシュのクリア",
      message:
        "キャッシュをクリアしてすべてのデータを再取得しますか？\nサーバーからすべてのデータを再取得します。",
      type: "normal",
      confirmText: "クリアする",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        const authEntries: [string, string][] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key?.startsWith(AUTH_KEY_PREFIX)) {
            authEntries.push([key, localStorage.getItem(key)!]);
          }
        }
        localStorage.clear();
        authEntries.forEach(([k, v]) => localStorage.setItem(k, v));
        // キャッシュを捨てたあとは Chatis の /goto から入り直す。
        // 単純なリロードでは temp token が無いため認証できない。
        window.location.href = buildGotoUrl({
          app: "todo",
          roomId,
          color,
          isDarkMode: isDarkModeFromUrl(),
          shortcut: isShortcutLaunch(),
        });
      },
    });
  };

  // テストモード（開発中機能の表示切替。localStorage で永続化）
  const isTestMode = useTestMode();

  const [shortcutMessage, setShortcutMessage] = useState<string | null>(null);
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  // ショートカットは TODO ではなく Chatis の /goto を指す。
  // TODO を直接開いても temp token が無く認証できないため、Chatis 側に
  // ログイン確認とトークン発行をしてもらってから TODO へ着地させる。
  const getShortcutUrl = () => {
    return buildGotoUrl({
      app: "todo",
      roomId,
      color,
      isDarkMode: isDarkModeFromUrl(),
      shortcut: true,
    });
  };

  const handleCreateShortcut = () => {
    const appUrl = getShortcutUrl();
    const content = `[InternetShortcut]\r\nURL=${appUrl}\r\n`;
    const blob = new Blob([content], { type: "text/plain" });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `ChatisTODO (Room${roomId}).url`;
    a.click();
    URL.revokeObjectURL(blobUrl);
    setShortcutMessage(
      "ショートカットファイルをダウンロードしました。デスクトップに移動してください。",
    );
  };

  const handleCopyUrl = async () => {
    const url = getShortcutUrl();
    let success = false;
    try {
      await navigator.clipboard.writeText(url);
      success = true;
    } catch {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.style.cssText = "position:fixed;opacity:0;pointer-events:none";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        success = document.execCommand("copy");
        document.body.removeChild(textarea);
      } catch {
        success = false;
      }
    }
    if (success) {
      setIsCopied(true);
      setCopyMessage(null);
      setTimeout(() => setIsCopied(false), 2000);
    } else {
      setCopyMessage("コピーに失敗しました。");
      setTimeout(() => setCopyMessage(null), 3000);
    }
  };

  const handleAddTagClick = () => {
    if (newTagName.trim() && !tags.find((t) => t.name === newTagName.trim())) {
      onAddTag(newTagName.trim(), newTagColor);
      setNewTagName("");
      setNewTagColor(PALETTE_COLORS[0]);
    }
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleAddTagClick();
    }
  };

  const startEditTag = (tag: Tag) => {
    setEditingTagId(tag.id);
    setEditName(tag.name);
    setEditColor(tag.color);
  };

  const cancelEditTag = () => {
    setEditingTagId(null);
    setEditName("");
    setEditColor("");
  };

  const handleSaveEditTag = () => {
    if (editingTagId == null || !editName.trim()) return;
    const duplicate = tags.find(
      (t) => t.id !== editingTagId && t.name === editName.trim(),
    );
    if (duplicate) return;
    onUpdateTag(editingTagId, editName.trim(), editColor);
    setEditingTagId(null);
    setEditName("");
    setEditColor("");
  };

  const handleEditTagKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSaveEditTag();
    } else if (e.key === "Escape") {
      cancelEditTag();
    }
  };

  const paletteColors = Object.keys(PaletteColorHex) as PaletteColor[];

  const getTabClassName = (tabName: TabKey) => {
    return `px-2 py-1.5 text-xs font-semibold rounded-t-lg transition-colors whitespace-nowrap ${
      activeTab === tabName
        ? "border-b-2 bg-white dark:bg-[#1e1e1e]"
        : "text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-[#2a2a2a] hover:bg-gray-200 dark:hover:bg-[#333]"
    }`;
  };

  // --- Tab content renderer (shared between PC tabs and mobile accordion) ---
  const renderTabContent = (tab: TabKey) => {
    if (tab === "status") {
      return (
        <div className="p-6 space-y-6">
          <div className="bg-blue-50 dark:bg-[#1e2733] rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed border border-blue-100 dark:border-[#2d3f52]">
            <p className="font-semibold mb-1">ステータスとは</p>
            <p>
              タスクの進捗状態を表すラベルです。各チャットルームごとに設定ができます（同一ルーム内のプロジェクト間では共通のもの）。
            </p>
            <p className="mt-2">
              ドラッグで表示順を並び替えられます。
              「完了」などの標準ステータスは削除できません。
            </p>
          </div>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="statuses">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-3"
                >
                  {statuses.map((status, index) => (
                    <Draggable
                      key={status.roomStatusId}
                      draggableId={String(status.roomStatusId)}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className="group flex items-center justify-between p-2 bg-gray-50 dark:bg-[#2a2a2a] rounded-md border dark:border-[#333]"
                        >
                          <div className="flex items-center gap-3">
                            <div {...provided.dragHandleProps}>
                              <DragIndicatorIcon className="text-gray-400 cursor-grab" />
                            </div>
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenColorPickerId(
                                    openColorPickerId === status.roomStatusId
                                      ? null
                                      : status.roomStatusId,
                                  );
                                }}
                                className="w-6 h-6 rounded-full shadow-md"
                                style={{
                                  backgroundColor: PaletteColorHex[status.color],
                                }}
                                aria-label="Change color"
                              />
                              {openColorPickerId === status.roomStatusId && (
                                <div
                                  ref={colorPickerRef}
                                  className="absolute left-0 top-full mt-2 p-2 bg-white dark:bg-[#2a2a2a] rounded-md shadow-lg z-10 grid grid-cols-5 gap-2 w-40"
                                >
                                  {paletteColors.map((colorOption) => (
                                    <button
                                      key={colorOption}
                                      onClick={() => {
                                        handleUpdate(status.roomStatusId, {
                                          color: colorOption,
                                        });
                                        setOpenColorPickerId(null);
                                      }}
                                      className="w-6 h-6 rounded-full transition-transform hover:scale-110 shadow-md flex items-center justify-center"
                                      style={{
                                        backgroundColor: PaletteColorHex[colorOption],
                                      }}
                                      aria-label={colorOption}
                                    >
                                      {status.color === colorOption && (
                                        <CheckIcon
                                          style={{ color: "white", fontSize: "1rem" }}
                                        />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <span
                              className="font-semibold"
                              style={{ color: PaletteColorHex[status.color] }}
                            >
                              {status.statusName}
                            </span>
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenEffectPickerId(
                                    openEffectPickerId === status.roomStatusId
                                      ? null
                                      : status.roomStatusId,
                                  );
                                }}
                                className={`transition-opacity text-gray-400 hover:text-black dark:hover:text-white ${
                                  status.action === "None"
                                    ? "opacity-0 group-hover:opacity-100"
                                    : "opacity-100"
                                }`}
                                title="演出を設定"
                              >
                                {(() => {
                                  const effect = availableEffects.find(
                                    (e) => e.name === status.action,
                                  );
                                  const EffectIcon =
                                    status.action === "None"
                                      ? MotionPhotosAutoSharpIcon
                                      : effect?.icon || MotionPhotosAutoSharpIcon;
                                  return <EffectIcon fontSize="small" />;
                                })()}
                              </button>
                              {openEffectPickerId === status.roomStatusId && (
                                <div
                                  ref={effectPickerRef}
                                  className="absolute left-0 top-full mt-2 p-2 bg-white dark:bg-[#2a2a2a] rounded-md shadow-lg z-10 grid grid-cols-3 gap-2 w-48"
                                >
                                  {availableEffects.map((effect) => (
                                    <button
                                      key={effect.name}
                                      onClick={() => {
                                        handleUpdate(status.roomStatusId, {
                                          action: effect.name,
                                        });
                                        setOpenEffectPickerId(null);
                                      }}
                                      className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-[#333] flex flex-col items-center text-gray-700 dark:text-gray-300"
                                      title={effect.name}
                                    >
                                      <effect.icon />
                                      <span className="text-[10px]">{effect.name}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {status.isDeletable && (
                              <button
                                onClick={() => handleDeleteStatus(status.roomStatusId)}
                                className="text-sm text-red-600 hover:underline"
                              >
                                削除
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <div className="pt-4">
            <p className="text-sm font-semibold mb-2">新しいステータスを追加</p>
            <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-[#2a2a2a] rounded-md border dark:border-[#333]">
              <input
                type="text"
                value={newStatusName}
                onChange={(e) => setNewStatusName(e.target.value)}
                placeholder="ステータス名"
                className="p-2 border dark:border-[#444] rounded flex-grow dark:bg-[#1e1e1e] dark:text-gray-200"
              />
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsNewColorPickerOpen(!isNewColorPickerOpen);
                  }}
                  className="w-8 h-8 rounded-full shadow-md border"
                  style={{ backgroundColor: PaletteColorHex[newStatusColor] }}
                  aria-label="Change color for new status"
                />
                {isNewColorPickerOpen && (
                  <div
                    ref={newColorPickerRef}
                    className="absolute right-0 bottom-full mb-2 p-2 bg-white dark:bg-[#2a2a2a] rounded-md shadow-lg z-10 grid grid-cols-5 gap-2 w-40"
                  >
                    {paletteColors.map((colorOption) => (
                      <button
                        key={colorOption}
                        onClick={() => {
                          setNewStatusColor(colorOption);
                          setIsNewColorPickerOpen(false);
                        }}
                        className="w-6 h-6 rounded-full transition-transform hover:scale-110 shadow-md flex items-center justify-center"
                        style={{ backgroundColor: PaletteColorHex[colorOption] }}
                        aria-label={colorOption}
                      >
                        {newStatusColor === colorOption && (
                          <CheckIcon style={{ color: "white", fontSize: "1rem" }} />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={handleAddStatus}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300"
                disabled={!newStatusName.trim()}
              >
                追加
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (tab === "tags") {
      return (
        <div className="p-6 space-y-4">
          <div className="bg-blue-50 dark:bg-[#1e2733] rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed border border-blue-100 dark:border-[#2d3f52]">
            <p className="font-semibold mb-1">タグとは</p>
            <p>
              タスクに付けられるラベルです。各チャットルームごとに設定ができます（同一ルーム内のプロジェクト間では共通のもの）。
            </p>
            <p className="mt-2">
              1つのタスクに複数のタグを付けられます。色を設定して
              見分けやすくできます。
            </p>
          </div>
          <div className="space-y-4">
            <div>
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyPress={handleTagInputKeyPress}
                className="w-full p-2 border dark:border-[#444] rounded dark:bg-[#1e1e1e] dark:text-gray-200 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                placeholder="新しいタグ名"
              />
            </div>
            <div className="grid grid-cols-5 gap-2 p-2 justify-items-center">
              {PALETTE_COLORS.map((color) => (
                <button
                  key={color}
                  style={{ backgroundColor: color }}
                  onClick={() => setNewTagColor(color)}
                  className={`w-8 h-8 rounded-full cursor-pointer transition-all transform hover:scale-110 shadow-md ${
                    newTagColor === color
                      ? "ring-2 ring-offset-2 ring-blue-500"
                      : "ring-2 ring-white"
                  }`}
                  aria-label={`Color ${color}`}
                />
              ))}
            </div>
            <button
              onClick={handleAddTagClick}
              className="w-full px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400"
              disabled={!newTagName.trim()}
            >
              タグを追加
            </button>
          </div>

          <div className="flex-grow space-y-2 max-h-60 overflow-y-auto mt-4 border-t dark:border-[#333] pt-4">
            {tags.length > 0 ? (
              tags.map((tag) =>
                editingTagId === tag.id ? (
                  <div key={tag.id} className="space-y-2 p-2 rounded bg-gray-50 dark:bg-[#2a2a2a]">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={handleEditTagKeyPress}
                      className="w-full p-1.5 border dark:border-[#444] rounded text-sm dark:bg-[#1e1e1e] dark:text-gray-200 focus:ring-2 focus:ring-blue-400 focus:outline-none"
                      autoFocus
                    />
                    <div className="grid grid-cols-5 gap-1.5 justify-items-center">
                      {PALETTE_COLORS.map((color) => (
                        <button
                          key={color}
                          style={{ backgroundColor: color }}
                          onClick={() => setEditColor(color)}
                          className={`w-6 h-6 rounded-full cursor-pointer transition-all transform hover:scale-110 shadow-sm ${
                            editColor === color
                              ? "ring-2 ring-offset-1 ring-blue-500"
                              : "ring-1 ring-white"
                          }`}
                          aria-label={`Color ${color}`}
                        />
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEditTag}
                        disabled={!editName.trim()}
                        className="flex-1 px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 disabled:bg-gray-400 flex items-center justify-center gap-1"
                      >
                        <CheckIcon fontSize="inherit" />
                        保存
                      </button>
                      <button
                        onClick={cancelEditTag}
                        className="flex-1 px-2 py-1 bg-gray-300 dark:bg-[#444] text-gray-700 dark:text-gray-200 rounded text-xs hover:bg-gray-400 dark:hover:bg-[#555] flex items-center justify-center gap-1"
                      >
                        キャンセル
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    key={tag.id}
                    className="flex justify-between items-center p-2 rounded"
                  >
                    <div
                      style={{
                        color: tag.color,
                        backgroundColor: `${tag.color}20`,
                      }}
                      className="px-3 py-1 rounded-full text-xs font-semibold"
                    >
                      #{tag.name}
                    </div>
                    <div className="flex gap-2 items-center">
                      <button
                        onClick={() => startEditTag(tag)}
                        className="text-sm text-blue-600 border border-blue-400 rounded-lg px-2 py-0.5 hover:bg-blue-50"
                      >
                        編集
                      </button>
                      <button
                        onClick={() => onDeleteTag(tag.id)}
                        className="text-sm text-red-600 border border-red-400 rounded-lg px-2 py-0.5 hover:bg-red-50"
                      >
                        削除
                      </button>
                    </div>
                  </div>
                ),
              )
            ) : (
              <div className="text-center text-gray-500 dark:text-gray-400 pt-4">
                登録されているタグはありません。
              </div>
            )}
          </div>
        </div>
      );
    }

    if (tab === "custom") {
      return (
        <div className="p-4 space-y-3">
          <div className="bg-blue-50 dark:bg-[#1e2733] rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed border border-blue-100 dark:border-[#2d3f52]">
            <p className="font-semibold mb-1">カスタム項目とは</p>
            <p>
              プロジェクトごとに独自の入力欄を追加できる機能です。
              標準の項目だけでは足りない情報をタスクに持たせられます。
            </p>
            <p className="mt-2">
              「テキスト」で自由入力、「選択肢」で決まった候補から選ぶ形式を
              設定できます。項目はプロジェクトごとに管理されます。
            </p>
          </div>
          {sortedListsForCustom.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-gray-400 py-6 text-sm">
              プロジェクトがありません。
            </div>
          ) : (
            sortedListsForCustom.map((list) => {
              const listId = parseInt(list.id, 10);
              const columns = columnsByListId[listId] || [];
              const isActive = listId === activeListId;
              const isAddingHere = addingToListId === listId;

              return (
                <div key={list.id} className="border dark:border-[#333] rounded-lg overflow-hidden">
                  <div
                    className={`px-3 py-2 flex items-center gap-2 border-b dark:border-[#333] ${isActive ? "bg-blue-50 dark:bg-[#1e2733]" : "bg-gray-50 dark:bg-[#2a2a2a]"}`}
                  >
                    <span className="font-semibold text-sm">{list.name}</span>
                    {isActive && (
                      <span className="text-xs text-blue-500 dark:text-blue-300 bg-blue-100 dark:bg-[#1e2733] px-1.5 py-0.5 rounded">
                        開いているプロジェクト
                      </span>
                    )}
                  </div>

                  <div className="p-2 space-y-1">
                    {columns.length === 0 && !isAddingHere && (
                      <p className="text-xs text-gray-400 text-center py-2">
                        カスタム項目がまだありません。
                      </p>
                    )}
                    {columns.map((col) => (
                      <div key={col.customColumnId} className="border dark:border-[#333] rounded overflow-hidden">
                        {editingColumnId === col.customColumnId ? (
                          <div className="p-3 space-y-3 bg-gray-50 dark:bg-[#2a2a2a]">
                            <input
                              type="text"
                              value={editColumnForm.columnName}
                              onChange={(e) => setEditColumnForm((f) => ({ ...f, columnName: e.target.value }))}
                              placeholder="項目名"
                              className="w-full p-2 border dark:border-[#444] rounded text-sm dark:bg-[#1e1e1e] dark:text-gray-200"
                            />
                            <div className="flex gap-3">
                              <label className="flex items-center gap-1 text-sm cursor-pointer">
                                <input
                                  type="radio"
                                  checked={editColumnForm.columnType === "text"}
                                  onChange={() => setEditColumnForm((f) => ({ ...f, columnType: "text" }))}
                                />
                                テキスト
                              </label>
                              <label className="flex items-center gap-1 text-sm cursor-pointer">
                                <input
                                  type="radio"
                                  checked={editColumnForm.columnType === "select"}
                                  onChange={() =>
                                    setEditColumnForm((f) => ({
                                      ...f,
                                      columnType: "select",
                                      options: f.options.length > 0 ? f.options : [""],
                                    }))
                                  }
                                />
                                選択肢
                              </label>
                            </div>
                            {editColumnForm.columnType === "select" && (
                              <div className="space-y-1">
                                {editColumnForm.options.map((opt, i) => (
                                  <div key={i} className="flex gap-1">
                                    <input
                                      type="text"
                                      value={opt}
                                      onChange={(e) => {
                                        const opts = [...editColumnForm.options];
                                        opts[i] = e.target.value;
                                        setEditColumnForm((f) => ({ ...f, options: opts }));
                                      }}
                                      placeholder={`選択肢 ${i + 1}`}
                                      className="flex-1 p-1.5 border dark:border-[#444] rounded text-sm dark:bg-[#1e1e1e] dark:text-gray-200"
                                    />
                                    {editColumnForm.options.length > 1 && (
                                      <button
                                        onClick={() =>
                                          setEditColumnForm((f) => ({
                                            ...f,
                                            options: f.options.filter((_, j) => j !== i),
                                          }))
                                        }
                                        className="text-red-400 hover:text-red-600 px-1"
                                      >
                                        ✕
                                      </button>
                                    )}
                                  </div>
                                ))}
                                <div className="flex items-center justify-between">
                                  <button
                                    onClick={() =>
                                      setEditColumnForm((f) => ({ ...f, options: [...f.options, ""] }))
                                    }
                                    className="text-xs text-blue-500 hover:underline"
                                  >
                                    + 選択肢を追加
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (bulkOptionTarget === "edit") {
                                        closeBulkOptionInput();
                                      } else {
                                        setBulkOptionTarget("edit");
                                        setBulkOptionText("");
                                      }
                                    }}
                                    className="text-xs text-blue-500 hover:underline"
                                  >
                                    一括作成
                                  </button>
                                </div>
                                {bulkOptionTarget === "edit" && (
                                  <div className="space-y-1 pt-1">
                                    <textarea
                                      value={bulkOptionText}
                                      onChange={(e) => setBulkOptionText(e.target.value)}
                                      rows={4}
                                      placeholder={"1行につき1つの選択肢を入力\n例:\n開発\n営業"}
                                      className="w-full p-1.5 border dark:border-[#444] rounded text-sm dark:bg-[#1e1e1e] dark:text-gray-200 resize-y"
                                      autoFocus
                                    />
                                    <div className="flex gap-2">
                                      <button
                                        onClick={applyBulkOptions}
                                        disabled={!bulkOptionText.trim()}
                                        className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 disabled:bg-gray-300"
                                      >
                                        選択肢に反映
                                      </button>
                                      <button
                                        onClick={closeBulkOptionInput}
                                        className="px-2 py-1 bg-gray-200 dark:bg-[#333] text-xs text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-[#3d3d3d]"
                                      >
                                        キャンセル
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            {columnError && <p className="text-xs text-red-500">{columnError}</p>}
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleUpdateColumn(col.customColumnId)}
                                disabled={savingColumn}
                                className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 disabled:bg-gray-300"
                              >
                                保存
                              </button>
                              <button
                                onClick={() => { setEditingColumnId(null); setColumnError(null); closeBulkOptionInput(); }}
                                className="px-3 py-1.5 bg-gray-200 dark:bg-[#333] text-sm text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-[#3d3d3d]"
                              >
                                キャンセル
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between p-2.5">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{col.columnName}</span>
                              <span className="text-xs text-gray-400 bg-gray-100 dark:bg-[#333] px-1.5 py-0.5 rounded">
                                {col.columnType === "text" ? "テキスト" : "選択肢"}
                              </span>
                              {col.columnType === "select" && col.options.length > 0 && (
                                <span className="text-xs text-gray-400">
                                  ({col.options.map((o) => o.optionValue).join(", ")})
                                </span>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => startEditColumn(col)}
                                className="text-blue-500 hover:text-blue-700 p-1"
                                title="編集"
                              >
                                <EditIcon fontSize="small" />
                              </button>
                              <button
                                onClick={() => handleDeleteColumn(col.customColumnId)}
                                className="text-red-400 hover:text-red-600 p-1"
                                title="削除"
                              >
                                <DeleteIcon fontSize="small" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {isAddingHere ? (
                      <div className="border dark:border-[#333] rounded p-3 space-y-3 bg-blue-50 dark:bg-[#1e2733] mt-1">
                        <input
                          type="text"
                          value={columnForm.columnName}
                          onChange={(e) => setColumnForm((f) => ({ ...f, columnName: e.target.value }))}
                          placeholder="項目名"
                          className="w-full p-2 border dark:border-[#444] rounded text-sm dark:bg-[#1e1e1e] dark:text-gray-200"
                          autoFocus
                        />
                        <div className="flex gap-3">
                          <label className="flex items-center gap-1 text-sm cursor-pointer">
                            <input
                              type="radio"
                              checked={columnForm.columnType === "text"}
                              onChange={() => setColumnForm((f) => ({ ...f, columnType: "text" }))}
                            />
                            テキスト
                          </label>
                          <label className="flex items-center gap-1 text-sm cursor-pointer">
                            <input
                              type="radio"
                              checked={columnForm.columnType === "select"}
                              onChange={() =>
                                setColumnForm((f) => ({
                                  ...f,
                                  columnType: "select",
                                  options: f.options.length > 0 ? f.options : [""],
                                }))
                              }
                            />
                            選択肢
                          </label>
                        </div>
                        {columnForm.columnType === "select" && (
                          <div className="space-y-1">
                            {columnForm.options.map((opt, i) => (
                              <div key={i} className="flex gap-1">
                                <input
                                  type="text"
                                  value={opt}
                                  onChange={(e) => {
                                    const opts = [...columnForm.options];
                                    opts[i] = e.target.value;
                                    setColumnForm((f) => ({ ...f, options: opts }));
                                  }}
                                  placeholder={`選択肢 ${i + 1}`}
                                  className="flex-1 p-1.5 border rounded text-sm"
                                />
                                {columnForm.options.length > 1 && (
                                  <button
                                    onClick={() =>
                                      setColumnForm((f) => ({
                                        ...f,
                                        options: f.options.filter((_, j) => j !== i),
                                      }))
                                    }
                                    className="text-red-400 hover:text-red-600 px-1"
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            ))}
                            <div className="flex items-center justify-between">
                              <button
                                onClick={() =>
                                  setColumnForm((f) => ({ ...f, options: [...f.options, ""] }))
                                }
                                className="text-xs text-blue-500 hover:underline"
                              >
                                + 選択肢を追加
                              </button>
                              <button
                                onClick={() => {
                                  if (bulkOptionTarget === "add") {
                                    closeBulkOptionInput();
                                  } else {
                                    setBulkOptionTarget("add");
                                    setBulkOptionText("");
                                  }
                                }}
                                className="text-xs text-blue-500 hover:underline"
                              >
                                一括作成
                              </button>
                            </div>
                            {bulkOptionTarget === "add" && (
                              <div className="space-y-1 pt-1">
                                <textarea
                                  value={bulkOptionText}
                                  onChange={(e) => setBulkOptionText(e.target.value)}
                                  rows={4}
                                  placeholder={"1行につき1つの選択肢を入力\n例:\n開発\n営業"}
                                  className="w-full p-1.5 border dark:border-[#444] rounded text-sm dark:bg-[#1e1e1e] dark:text-gray-200 resize-y"
                                  autoFocus
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={applyBulkOptions}
                                    disabled={!bulkOptionText.trim()}
                                    className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 disabled:bg-gray-300"
                                  >
                                    選択肢に反映
                                  </button>
                                  <button
                                    onClick={closeBulkOptionInput}
                                    className="px-2 py-1 bg-gray-200 dark:bg-[#333] text-xs text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-[#3d3d3d]"
                                  >
                                    キャンセル
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {columnError && <p className="text-xs text-red-500">{columnError}</p>}
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAddColumn(listId)}
                            disabled={savingColumn || !columnForm.columnName.trim()}
                            className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 disabled:bg-gray-300"
                          >
                            追加
                          </button>
                          <button
                            onClick={() => {
                              setAddingToListId(null);
                              setColumnForm(emptyColumnForm());
                              setColumnError(null);
                              closeBulkOptionInput();
                            }}
                            className="px-3 py-1.5 bg-gray-200 dark:bg-[#333] text-sm text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-[#3d3d3d]"
                          >
                            キャンセル
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setAddingToListId(listId);
                          setColumnForm(emptyColumnForm());
                          setColumnError(null);
                          closeBulkOptionInput();
                        }}
                        className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600 py-1 px-1 mt-1"
                      >
                        <AddIcon fontSize="small" />
                        カスタム項目を追加
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      );
    }

    if (tab === "autoReminder") {
      const PRIORITIES = ["高", "中", "低", "なし"];
      const ALWAYS_EXCLUDED = ["完了", "非表示"];
      const selectableStatuses = statuses.length > 0
        ? statuses.filter((s) => !ALWAYS_EXCLUDED.includes(s.statusName)).map((s) => s.statusName)
        : ["未対応", "対応中", "待機"];

      return (
        <div className="p-4 space-y-6">
          <div className="bg-blue-50 dark:bg-[#1e2733] rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed border border-blue-100 dark:border-[#2d3f52]">
            <p className="font-semibold mb-1">自動リマインダーとは</p>
            <p>
              プロジェクトごとに、期限のあるタスクへリマインダーを自動で設定する機能です。
              1つのプロジェクトに複数のルールを作れます。各ルールで対象の
              優先度・ステータス・タグと「期限の何日前」を決めておくと、
              条件に合うタスクへ自動でリマインダーが付きます。
            </p>
            <p className="mt-2">
              条件が合うルールが複数あるタスクには、ルールごとにリマインダーが付きます
              （例: タグAは2日前・タグBは1週間前 → 両方付いたタスクは両方リマインド）。
            </p>
            <p className="mt-2">
              タスクの期限・優先度・ステータス・タグを変更すると、リマインダーも自動で付け直されます。
              完了・非表示のタスクには通知されません。
            </p>
          </div>
          {sortedListsForCustom.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">プロジェクトがありません。</p>
          ) : (
            sortedListsForCustom.map((list) => {
              const listId = parseInt(list.id, 10);
              if (listId <= 0) return null;
              const rules = autoReminderRules[listId] ?? [];
              const isLoading = loadingAutoReminder[listId];

              return (
                <div key={listId} className="border dark:border-[#333] rounded-lg overflow-hidden">
                  <div className="bg-gray-50 dark:bg-[#2a2a2a] px-4 py-2 border-b dark:border-[#333] flex items-center justify-between">
                    <span className="font-semibold text-sm text-gray-700 dark:text-gray-200">{list.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {rules.length > 0 ? `${rules.length}件のルール` : "ルールなし"}
                    </span>
                  </div>
                  <div className="p-4 space-y-4">
                    {isLoading ? (
                      <p className="text-sm text-gray-400">読み込み中...</p>
                    ) : (
                      <>
                        {rules.length === 0 && (
                          <p className="text-sm text-gray-400">
                            まだルールがありません。「ルールを追加」から作成してください。
                          </p>
                        )}

                        {rules.map((rule) => {
                          const ruleId = rule.autoReminderRuleId;
                          const isSaving = savingAutoRuleId === ruleId;
                          const saveResult = autoRuleSaveResult[ruleId];

                          const togglePriority = (p: string) => {
                            const next = rule.enabledPriorities.includes(p)
                              ? rule.enabledPriorities.filter((x) => x !== p)
                              : [...rule.enabledPriorities, p];
                            updateAutoReminderRuleForm(listId, ruleId, { enabledPriorities: next });
                          };
                          const toggleStatus = (s: string) => {
                            const next = rule.enabledStatuses.includes(s)
                              ? rule.enabledStatuses.filter((x) => x !== s)
                              : [...rule.enabledStatuses, s];
                            updateAutoReminderRuleForm(listId, ruleId, { enabledStatuses: next });
                          };
                          const toggleTag = (tagId: number) => {
                            const next = rule.enabledTags.includes(tagId)
                              ? rule.enabledTags.filter((x) => x !== tagId)
                              : [...rule.enabledTags, tagId];
                            updateAutoReminderRuleForm(listId, ruleId, { enabledTags: next });
                          };

                          return (
                            <div
                              key={ruleId}
                              className={`border rounded-lg p-3 space-y-3 ${
                                rule.isEnabled ? "border-gray-200 dark:border-[#333]" : "border-gray-200 dark:border-[#333] bg-gray-50/60 dark:bg-[#2a2a2a]/60"
                              }`}
                            >
                              {/* ヘッダー: ルール名・有効トグル */}
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={rule.ruleName}
                                  placeholder="ルール名（任意）"
                                  onChange={(e) =>
                                    updateAutoReminderRuleForm(listId, ruleId, { ruleName: e.target.value })
                                  }
                                  className="flex-1 border dark:border-[#444] rounded px-2 py-1 text-sm dark:bg-[#1e1e1e] dark:text-gray-200"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateAutoReminderRuleForm(listId, ruleId, { isEnabled: !rule.isEnabled })
                                  }
                                  className="flex items-center gap-1.5 flex-shrink-0"
                                  title={rule.isEnabled ? "有効" : "無効"}
                                >
                                  <div
                                    className={`relative w-9 h-5 rounded-full transition-colors ${
                                      rule.isEnabled ? "bg-blue-500" : "bg-gray-300"
                                    }`}
                                  >
                                    <div
                                      className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transform transition-transform ${
                                        rule.isEnabled ? "translate-x-4" : "translate-x-0.5"
                                      }`}
                                    />
                                  </div>
                                  <span className="text-xs text-gray-600 dark:text-gray-400">
                                    {rule.isEnabled ? "有効" : "無効"}
                                  </span>
                                </button>
                              </div>

                              {/* ステップ1: 実行タイミング */}
                              <div className="border border-gray-200 dark:border-[#3a3a3a] rounded-lg overflow-hidden">
                                <p className="bg-gray-50 dark:bg-[#2a2a2a] px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 border-b border-gray-200 dark:border-[#3a3a3a]">
                                  実行タイミング
                                </p>
                                <div className="p-3 flex items-center gap-2 flex-wrap">
                                  <span className="text-sm text-gray-600 dark:text-gray-400">期限の</span>
                                  <input
                                    type="number"
                                    min={0}
                                    max={365}
                                    value={rule.daysBeforeDeadline}
                                    onChange={(e) =>
                                      updateAutoReminderRuleForm(listId, ruleId, {
                                        daysBeforeDeadline: Math.max(0, Math.min(365, Number(e.target.value))),
                                      })
                                    }
                                    className="w-14 border dark:border-[#444] rounded px-2 py-1 text-sm text-center dark:bg-[#1e1e1e] dark:text-gray-200"
                                  />
                                  <span className="text-sm text-gray-600 dark:text-gray-400">日前の</span>
                                  <select
                                    value={rule.reminderHour}
                                    onChange={(e) =>
                                      updateAutoReminderRuleForm(listId, ruleId, {
                                        reminderHour: Number(e.target.value),
                                      })
                                    }
                                    className="border dark:border-[#444] rounded px-2 py-1 text-sm dark:bg-[#1e1e1e] dark:text-gray-200"
                                  >
                                    {Array.from({ length: 24 }, (_, i) => (
                                      <option key={i} value={i}>
                                        {i}時
                                      </option>
                                    ))}
                                  </select>
                                  <span className="text-sm text-gray-600 dark:text-gray-400">にリマインドする</span>
                                </div>
                              </div>

                              {/* ステップ2: 対象タスクの条件 */}
                              <div className="border border-gray-200 dark:border-[#3a3a3a] rounded-lg overflow-hidden">
                                <p className="bg-gray-50 dark:bg-[#2a2a2a] px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 border-b border-gray-200 dark:border-[#3a3a3a]">
                                  対象タスクの条件
                                </p>
                                <div className="p-3 space-y-3">
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    以下の条件を<span className="font-semibold">すべて満たす</span>タスクが対象になります。
                                  </p>

                                  {/* 優先度 */}
                                  <div className="space-y-1">
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">優先度</p>
                                    <div className="flex flex-wrap gap-2">
                                      {PRIORITIES.map((p) => (
                                        <label key={p} className="flex items-center gap-1 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={rule.enabledPriorities.includes(p)}
                                            onChange={() => togglePriority(p)}
                                            className="rounded"
                                          />
                                          <span className="text-sm">{p}</span>
                                        </label>
                                      ))}
                                    </div>
                                  </div>

                                  {/* ステータス */}
                                  <div className="space-y-1">
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">ステータス</p>
                                    <div className="flex flex-wrap gap-2">
                                      {selectableStatuses.map((s) => (
                                        <label key={s} className="flex items-center gap-1 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={rule.enabledStatuses.includes(s)}
                                            onChange={() => toggleStatus(s)}
                                            className="rounded"
                                          />
                                          <span className="text-sm">{s}</span>
                                        </label>
                                      ))}
                                    </div>
                                  </div>

                                  {/* タグ */}
                                  <div className="space-y-1">
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">タグ</p>
                                    {tags.length === 0 ? (
                                      <p className="text-xs text-gray-400">タグがありません。</p>
                                    ) : (
                                      <>
                                        <div className="flex flex-wrap items-center gap-2">
                                          {rule.enabledTags.map((tagId) => {
                                            const tag = tags.find((t) => t.id === tagId);
                                            if (!tag) return null;
                                            return (
                                              <span
                                                key={tag.id}
                                                className="flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full text-xs text-white"
                                                style={{ backgroundColor: tag.color }}
                                              >
                                                {tag.name}
                                                <button
                                                  type="button"
                                                  onClick={() => toggleTag(tag.id)}
                                                  aria-label={`タグ「${tag.name}」を外す`}
                                                  className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-black/20 leading-none"
                                                >
                                                  ✕
                                                </button>
                                              </span>
                                            );
                                          })}
                                          {rule.enabledTags.length === 0 && (
                                            <span className="text-xs text-gray-400">未指定（すべてのタスクが対象）</span>
                                          )}
                                          {tags.some((t) => !rule.enabledTags.includes(t.id)) && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                setTagPickerRuleId(tagPickerRuleId === ruleId ? null : ruleId)
                                              }
                                              className="px-2 py-0.5 rounded-full text-xs border border-dashed border-gray-400 dark:border-gray-500 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2f2f2f] transition-colors"
                                            >
                                              ＋ タグを追加
                                            </button>
                                          )}
                                        </div>
                                        {tagPickerRuleId === ruleId &&
                                          tags.some((t) => !rule.enabledTags.includes(t.id)) && (
                                            <div className="flex flex-wrap gap-2 p-2 border border-gray-200 dark:border-[#3a3a3a] rounded bg-gray-50 dark:bg-[#242424]">
                                              {tags
                                                .filter((t) => !rule.enabledTags.includes(t.id))
                                                .map((tag) => (
                                                  <button
                                                    key={tag.id}
                                                    type="button"
                                                    onClick={() => toggleTag(tag.id)}
                                                    className="px-2 py-0.5 rounded-full text-xs border bg-white dark:bg-[#1e1e1e] text-gray-600 dark:text-gray-300 transition-colors"
                                                    style={{ borderColor: tag.color }}
                                                  >
                                                    ＋ {tag.name}
                                                  </button>
                                                ))}
                                            </div>
                                          )}
                                        {rule.enabledTags.length >= 2 && (
                                          <p className="text-xs text-amber-600 dark:text-amber-400">
                                            ※ 選択した{rule.enabledTags.length}個のタグを
                                            <span className="font-semibold">すべて</span>
                                            持つタスクだけが対象です（「いずれか1つ」ではありません）
                                          </p>
                                        )}
                                      </>
                                    )}
                                  </div>

                                  <p className="text-xs text-gray-400 dark:text-gray-500">
                                    ※ 完了・非表示ステータスのタスクにはリマインドされません。
                                  </p>
                                </div>
                              </div>

                              {/* 既存適用 */}
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={rule.applyToExisting}
                                  onChange={(e) =>
                                    updateAutoReminderRuleForm(listId, ruleId, {
                                      applyToExisting: e.target.checked,
                                    })
                                  }
                                  className="rounded"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  保存時に既存タスクにも適用する
                                </span>
                              </label>

                              {/* 操作 */}
                              <div className="flex items-center gap-2 pt-1">
                                <button
                                  onClick={() => handleSaveAutoReminderRule(listId, rule)}
                                  disabled={isSaving}
                                  className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 disabled:bg-gray-300 transition-colors"
                                >
                                  {isSaving ? "保存中..." : "保存"}
                                </button>
                                <button
                                  onClick={() => handleDeleteAutoReminderRule(listId, rule)}
                                  disabled={isSaving}
                                  className="px-3 py-1.5 bg-red-100 text-red-600 text-sm rounded hover:bg-red-200 disabled:opacity-50 transition-colors"
                                >
                                  削除
                                </button>
                                {saveResult === "ok" && (
                                  <span className="text-sm text-green-600">保存しました</span>
                                )}
                                {saveResult === "error" && (
                                  <span className="text-sm text-red-500">保存に失敗しました</span>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        <button
                          type="button"
                          onClick={() => addAutoReminderRule(listId)}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          ＋ ルールを追加
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      );
    }

    if (tab === "other") {
      return (
        <div className="p-6 space-y-6">
          <div className="space-y-2">
            <h4 className="font-semibold">デスクトップショートカット</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              デスクトップにショートカットを作成します。
              <br />
              次回からショートカットをダブルクリックするだけで、このアプリを開けます。
              <br />
              チャットアプリにログインした状態で開いてください。ログインしていない場合はログイン画面が表示されます。
            </p>
            <div className="space-y-2">
              <button
                onClick={handleCreateShortcut}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                デスクトップにショートカットを作成
              </button>
              {shortcutMessage && (
                <p className="text-sm text-green-600">{shortcutMessage}</p>
              )}
              <div>
                <button
                  onClick={() => setShowShortcutHelp(true)}
                  className="text-red-600 font-bold text-sm hover:text-red-700 underline"
                >
                  ダウンロード時にブロックされた場合はこちら
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <h4 className="font-semibold">キャッシュ</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              表示がおかしい場合や、最新の情報が反映されない場合に実行してください。
              <br />
              サーバーからすべてのデータを再取得します。
            </p>
            <div>
              <button
                onClick={handleCacheClear}
                className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
              >
                キャッシュを消して再取得
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <h4 className="font-semibold">テストモード</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              開発中の機能（タスク添付ファイル）を表示します。通常は OFF のままにしてください。
            </p>
            <FormControlLabel
              control={
                <Switch
                  checked={isTestMode}
                  onChange={(e) => setTestModeEnabled(e.target.checked)}
                />
              }
              label={
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {isTestMode ? "ON（添付ファイル機能を表示中）" : "OFF"}
                </span>
              }
            />
          </div>
        </div>
      );
    }

    return null;
  };

  // Tab definitions
  const tabs: { key: TabKey; label: string; Icon: React.ElementType }[] = [
    { key: "status", label: "ステータス設定", Icon: FlagTwoToneIcon },
    { key: "tags", label: "タグ設定", Icon: StyleTwoToneIcon },
    ...(activeListId != null && activeListId > 0
      ? [{ key: "custom" as TabKey, label: "カスタム項目", Icon: ExtensionTwoToneIcon }]
      : []),
    { key: "autoReminder", label: "自動リマインダー", Icon: AccessAlarmTwoToneIcon },
    { key: "other", label: "その他", Icon: SettingsTwoToneIcon },
  ];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 sm:p-4"
      onClick={() => onClose(settingsModified)}
    >
      {/* 確認ダイアログ */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        type={confirmDialog.type}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
        }
        color={color}
      />
      <div
        className="bg-white dark:bg-[#1e1e1e] text-gray-800 dark:text-gray-200 w-full h-full flex flex-col shadow-2xl border-2 border-white dark:border-black sm:rounded-xl sm:max-w-xl sm:h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div
          className="flex items-center justify-between px-4 py-3 flex-shrink-0"
          style={{
            background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
          }}
        >
          <div className="flex items-center gap-2 text-white">
            <SettingsTwoToneIcon fontSize="small" />
            <span className="font-bold text-sm">全体設定</span>
          </div>
          <button
            onClick={() => onClose(settingsModified)}
            className="p-1 rounded-full text-white hover:bg-white/20"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* PC: 横並びタブバー (sm以上で表示) */}
        <div className="hidden sm:block px-4 border-b dark:border-[#333] bg-gray-50 dark:bg-[#1e1e1e]">
          <div className="flex -mb-px overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={getTabClassName(tab.key)}
                style={
                  activeTab === tab.key
                    ? { borderColor: accent, color: accent }
                    : undefined
                }
                onClick={() => setActiveTab(tab.key)}
              >
                <span className="flex items-center gap-1">
                  <tab.Icon sx={{ fontSize: 16 }} />
                  {tab.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* PC: タブコンテンツ (sm以上で表示) */}
        <div className="hidden sm:block flex-grow overflow-y-auto">
          {renderTabContent(activeTab)}
        </div>

        {/* スマホ: アコーディオン (smより小さい画面で表示) */}
        <div className="sm:hidden flex-grow overflow-y-auto">
          {tabs.map((tab) => {
            const isOpen = mobileOpenSection === tab.key;
            return (
              <div key={tab.key} className="border-b dark:border-[#333] last:border-b-0">
                <button
                  className="w-full flex items-center justify-between px-4 py-3.5 text-left bg-white dark:bg-[#1e1e1e] active:bg-gray-50 dark:active:bg-[#2a2a2a]"
                  onClick={() => toggleMobileSection(tab.key)}
                >
                  <span className="flex items-center gap-2">
                    <tab.Icon fontSize="small" className="text-gray-500 dark:text-gray-400" />
                    <span className="font-medium text-gray-800 dark:text-gray-200">{tab.label}</span>
                  </span>
                  <ChevronRightIcon
                    fontSize="small"
                    className={`text-gray-400 transition-transform duration-200 ${
                      isOpen ? "rotate-90" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="border-t dark:border-[#333] bg-gray-50 dark:bg-[#1a1a1a]">
                    {renderTabContent(tab.key)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* フッター */}
        <div className="p-4 border-t dark:border-[#333] mt-auto flex justify-end bg-gray-50 dark:bg-[#1a1a1a]">
          <button
            onClick={() => onClose(settingsModified)}
            className="px-4 py-2 bg-gray-200 dark:bg-[#333] text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-[#3d3d3d]"
          >
            閉じる
          </button>
        </div>
      </div>

      {/* ショートカットヘルプダイアログ */}
      {showShortcutHelp && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={(e) => { e.stopPropagation(); setShowShortcutHelp(false); }}
        >
          <div
            className="bg-white dark:bg-[#1e1e1e] text-gray-800 dark:text-gray-200 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b dark:border-[#333] flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                ショートカットがブロックされた場合の対処方法
              </h3>
              <button
                onClick={() => setShowShortcutHelp(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <CloseIcon />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6">
              <div className="bg-yellow-50 dark:bg-[#2e2a1a] border border-yellow-200 dark:border-[#4a4326] rounded p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Edge使用時など、ブラウザのセキュリティ設定によっては本ショートカットの拡張子が「.url」であるためにブロックされてダウンロードが中止される場合があります。
                  <br />
                  その場合は、以下の手順で手動でショートカットを作成してください。
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2 p-4 bg-blue-50 dark:bg-[#1e2733] rounded">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-700 dark:text-gray-200">① URLをコピー</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        以下のボタンを押してショートカットのURLを表示します
                      </p>
                    </div>
                    <button
                      onClick={() => setShowUrl((v) => !v)}
                      className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors whitespace-nowrap"
                    >
                      URLを表示
                    </button>
                  </div>
                  {showUrl && (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={getShortcutUrl()}
                        onFocus={(e) => e.target.select()}
                        className="flex-1 p-2 text-xs border dark:border-[#444] rounded bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-200"
                      />
                      <button
                        onClick={handleCopyUrl}
                        className={`px-3 py-2 text-white rounded-md transition-colors whitespace-nowrap text-sm ${
                          isCopied
                            ? "bg-green-500 hover:bg-green-600"
                            : "bg-gray-500 hover:bg-gray-600"
                        }`}
                      >
                        {isCopied ? "コピーしました" : "コピー"}
                      </button>
                    </div>
                  )}
                  {copyMessage && (
                    <p className="text-sm text-red-600">{copyMessage}</p>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="border dark:border-[#333] rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center">
                      <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">
                        W
                      </span>
                      Windowsの場合
                    </h4>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-4">
                      <li>デスクトップ上で右クリック</li>
                      <li>「新規作成」→「ショートカット」を選択</li>
                      <li>
                        「項目の場所を入力してください」の欄に、コピーしたURLを貼り付け（Ctrl+V）
                      </li>
                      <li>「次へ」をクリック</li>
                      <li>
                        ショートカット名を入力（例：ChatisTODO Room{roomId}）
                      </li>
                      <li>「完了」をクリック</li>
                    </ol>
                  </div>

                  <div className="border dark:border-[#333] rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center">
                      <span className="bg-gray-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">
                        M
                      </span>
                      Macの場合
                    </h4>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 dark:text-gray-300 ml-4">
                      <li>Finderでデスクトップを開く</li>
                      <li>
                        メニューバーから「ファイル」→「新規スマートフォルダ」を選択
                      </li>
                      <li>
                        または、以下の方法でエイリアスを作成：
                        <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                          <li>
                            ブラウザのアドレスバーから、URLの左にある鍵アイコン（または地球儀アイコン）をデスクトップにドラッグ
                          </li>
                          <li>
                            または、「スクリプトエディタ」を開いて以下のスクリプトを実行：
                            <pre className="bg-gray-100 dark:bg-[#2a2a2a] p-2 rounded mt-2 text-xs overflow-x-auto">
                              {`tell application "Safari"
  make new document
  set URL of document 1 to "${getShortcutUrl()}"
end tell`}
                            </pre>
                          </li>
                        </ul>
                      </li>
                    </ol>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 italic">
                      ※
                      Macの場合、ブラウザをブックマークとして登録するか、Dockに追加する方法も便利です
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t dark:border-[#333] flex justify-end bg-gray-50 dark:bg-[#1a1a1a]">
              <button
                onClick={() => setShowShortcutHelp(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-[#333] text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-[#3d3d3d]"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
